/**
 * Reach against conversion — the chart that states the report's thesis
 * (2026-09-08).
 *
 * Ramos, on the first pass: "I am still just seeing regurgitated stats on bar
 * charts. Let's be visual digital data storytellers." A ranked bar can only
 * ever restate one column. This plots TWO, because the interesting thing about
 * this data is the relationship between them.
 *
 * THE ARGUMENT: a single visibility number hides two different failures with
 * opposite remedies — not being SEEN, and being seen but not CHOSEN. On the
 * axes together, every company lands in a quadrant and the client gets a
 * verdict rather than a rank. Antibodies Inc sits far left and high (reach 10%,
 * conversion 50%): well-regarded but unseen, so the money goes to reach, not to
 * messaging. Fisher Scientific sits bottom-right — everywhere, recommended by
 * almost nobody.
 *
 * The chart annotates its own conclusion: quadrants are labelled in plain
 * language, and a callout points at the client.
 *
 * FORM: scatter is an ALL-PAIRS colour form, so it must not carry a hue per
 * company — it uses emphasis instead (client accent, everyone else context
 * grey), which is also the honest encoding because one point is the subject and
 * the rest are context.
 */
import type { Positioning } from '@aurora/shared';
import { CONTEXT, TARGET } from './palette';

const W = 620;
const H = 400;
const PAD_L = 54;
const PAD_R = 18;
const PAD_T = 26;
const PAD_B = 46;
const PW = W - PAD_L - PAD_R;
const PH = H - PAD_T - PAD_B;

const pct = (v: number): string => {
  const p = v * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${Math.round(p)}%`;
};

const VERDICT: Record<string, string> = {
  leader: 'You are in the leaders’ quadrant: AI both surfaces you and endorses you.',
  'well-regarded-but-unseen':
    'You sit high but far left: when AI does surface you it endorses you at a competitive rate — the constraint is being surfaced at all. That points at reach, not at messaging.',
  'seen-but-not-chosen':
    'You sit right but low: AI surfaces you often and endorses you rarely — the constraint is what it says about you, not how often it finds you.',
  absent:
    'You sit in the lower left: AI rarely surfaces you, and rarely endorses you when it does.',
};

export function PositioningPlot({
  data,
}: {
  data: Positioning;
}): JSX.Element | null {
  const { points, reachMid, conversionMid, target, targetQuadrant } = data;
  if (points.length < 2) return null;

  const maxReach = Math.max(0.1, ...points.map((p) => p.reach));
  const xTop = Math.ceil((maxReach * 100) / 10) * 10 / 100;
  const x = (v: number): number => PAD_L + (v / xTop) * PW;
  const y = (v: number): number => PAD_T + PH - v * PH;

  const xTicks = [0, xTop / 2, xTop];
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  // Label only the points a reader needs named: the client, and the extremes.
  const byReach = [...points].sort((a, b) => b.reach - a.reach);
  const byConv = [...points].sort((a, b) => b.conversion - a.conversion);
  const named = new Set<string>();
  if (target) named.add(target.company);
  if (byReach[0]) named.add(byReach[0].company);
  if (byConv[0]) named.add(byConv[0].company);
  const worstConv = byConv[byConv.length - 1];
  if (worstConv && worstConv.reach > reachMid) named.add(worstConv.company);
  // Everyone in the leaders' quadrant gets named. A first version labelled only
  // the extremes, which left ABCAM — the company that both out-reaches and
  // out-converts the client, i.e. the one the report is really about —
  // anonymous in the corner that matters most.
  for (const p of points) {
    if (p.reach >= reachMid && p.conversion >= conversionMid)
      named.add(p.company);
  }

  /**
   * Label placement, de-collided.
   *
   * Two companies with near-identical scores put their labels on top of each
   * other (Cell Signaling Technology and Bio-Techne overlapped illegibly).
   *
   * A first attempt compared each label only to its immediate neighbour in
   * x-order and did nothing at all — MilliporeSigma sits BETWEEN those two on
   * the x axis, so the colliding pair was never adjacent and never tested.
   * Every already-placed label has to be checked, not just the previous one.
   */
  const labelled = points
    .filter((p) => named.has(p.company))
    .sort((a, b) => x(a.reach) - x(b.reach));
  const below = new Set<string>();
  const placed: Array<{ px: number; py: number }> = [];
  const HIT_X = 130;
  const HIT_Y = 15;
  for (const p of labelled) {
    const px = x(p.reach);
    const above = { px, py: y(p.conversion) - 13 };
    const under = { px, py: y(p.conversion) + 21 };
    const clashes = (c: { px: number; py: number }): boolean =>
      placed.some(
        (q) => Math.abs(q.px - c.px) < HIT_X && Math.abs(q.py - c.py) < HIT_Y,
      );
    if (clashes(above) && !clashes(under)) {
      below.add(p.company);
      placed.push(under);
    } else {
      placed.push(above);
    }
  }

  return (
    <figure className="positioning-plot">
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label="Companies plotted by how often AI surfaces them against how often it recommends them when it does"
      >
        {/* Quadrant fields, drawn first so everything sits above them. */}
        <rect
          x={PAD_L}
          y={PAD_T}
          width={x(reachMid) - PAD_L}
          height={y(conversionMid) - PAD_T}
          className="quadrant-highlight"
        />
        {yTicks.map((t) => (
          <g key={`y${t}`}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={y(t)}
              y2={y(t)}
              className="chart-gridline"
            />
            <text
              x={PAD_L - 7}
              y={y(t)}
              textAnchor="end"
              dominantBaseline="middle"
              className="chart-bar-label"
            >
              {pct(t)}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text
            key={`x${t}`}
            x={x(t)}
            y={PAD_T + PH + 16}
            textAnchor="middle"
            className="chart-bar-label"
          >
            {pct(t)}
          </text>
        ))}

        {/* The quadrant split. */}
        <line
          x1={x(reachMid)}
          x2={x(reachMid)}
          y1={PAD_T}
          y2={PAD_T + PH}
          className="quadrant-divider"
        />
        <line
          x1={PAD_L}
          x2={W - PAD_R}
          y1={y(conversionMid)}
          y2={y(conversionMid)}
          className="quadrant-divider"
        />

        {/* Plain-language quadrant labels — the chart explains itself. */}
        <text x={PAD_L + 6} y={PAD_T + 13} className="quadrant-label">
          Well regarded, rarely surfaced
        </text>
        <text
          x={W - PAD_R - 6}
          y={PAD_T + 13}
          textAnchor="end"
          className="quadrant-label"
        >
          Category leaders
        </text>
        <text
          x={PAD_L + 6}
          y={PAD_T + PH - 6}
          className="quadrant-label"
        >
          Rarely surfaced, rarely endorsed
        </text>
        <text
          x={W - PAD_R - 6}
          y={PAD_T + PH - 6}
          textAnchor="end"
          className="quadrant-label"
        >
          Surfaced often, endorsed rarely
        </text>

        {points.map((p) => {
          const r = p.isTarget ? 8 : 5.5;
          return (
            <g key={p.company}>
              <circle
                cx={x(p.reach)}
                cy={y(p.conversion)}
                r={r}
                fill={p.isTarget ? TARGET : CONTEXT}
                stroke={p.isTarget ? '#0b1020' : 'none'}
                strokeWidth={p.isTarget ? 2 : 0}
              >
                <title>
                  {p.company}: surfaced in {pct(p.reach)} of answers ({p.visible}
                  ), recommended in {pct(p.conversion)} of those ({p.recommended}
                  )
                </title>
              </circle>
              {named.has(p.company) && (
                <text
                  x={x(p.reach)}
                  y={
                    below.has(p.company)
                      ? y(p.conversion) + r + 13
                      : y(p.conversion) - r - 5
                  }
                  /* Anchor away from whichever edge the point is near, so a
                     long company name cannot run off the viewBox. */
                  textAnchor={
                    x(p.reach) > PAD_L + PW * 0.72
                      ? 'end'
                      : x(p.reach) < PAD_L + PW * 0.18
                        ? 'start'
                        : 'middle'
                  }
                  className={
                    p.isTarget ? 'point-label is-target' : 'point-label'
                  }
                >
                  {p.company.length > 22
                    ? `${p.company.slice(0, 21)}…`
                    : p.company}
                </text>
              )}
            </g>
          );
        })}

        {/* Axis titles. */}
        <text
          x={PAD_L + PW / 2}
          y={H - 6}
          textAnchor="middle"
          className="axis-title"
        >
          How often AI surfaces them →
        </text>
        <text
          x={14}
          y={PAD_T + PH / 2}
          textAnchor="middle"
          transform={`rotate(-90, 14, ${PAD_T + PH / 2})`}
          className="axis-title"
        >
          How often it recommends them →
        </text>
      </svg>
      {target && targetQuadrant && (
        <figcaption className="chart-verdict">
          <strong>{target.company}</strong> — {VERDICT[targetQuadrant]}
        </figcaption>
      )}
    </figure>
  );
}
